<script setup lang="ts">
import Big from 'big.js'
import type { Ref } from 'vue'
import { computed, inject, ref } from 'vue'
import { CanvasRenderer } from 'echarts/renderers'
import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import VChart from 'vue-echarts'

import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
} from 'echarts/components'
import { useQuery } from '@tanstack/vue-query'
import { todayNearest } from '@mp/api/mchTradeData/BpCompanyIndexDashboardController.js'
import { yunFormat } from '@mp/utils'

use([
  GridComponent,
  CanvasRenderer,
  LineChart,
  BarChart,
  TooltipComponent,
  ToolboxComponent,
])

const officeCode = inject<Ref<string>>('officeCode')

const chartRef = ref<InstanceType<typeof VChart> | null>(null)
const option = ref({
  tooltip: {
    trigger: 'axis',
  },
  legend: {
    data: ['交易金额(元)', '交易笔数'],
    bottom: 0,
  },
  grid: {
    top: 30,
    left: 50,
    right: 30,
    bottom: 60,
  },
  yAxis: [
    {
      type: 'value',
      name: '交易金额(元)',
      position: 'left',
      splitLine: {
        lineStyle: {
          color: '#eee',
          type: 'dashed',
        },
      },
    },
    {
      type: 'value',
      name: '交易笔数',
      position: 'right',
      splitLine: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      minInterval: 1,
    },
  ],
  xAxis: [
    {
      type: 'category',
      data: [] as string[],
      axisLabel: {
        color: '#999',
        fontSize: 10,
      },
      axisLine: {
        lineStyle: {
          color: '#eee',
        },
      },
    },
  ],
  series: [{
    name: '交易笔数',
    type: 'bar',
    data: [] as number[],
    itemStyle: {
      color: '#109AFF',
      borderRadius: 100,
    },
    yAxisIndex: 1,
  },
  {
    z: 10,
    name: '交易金额(元)',
    type: 'line',
    smooth: true,
    lineStyle: {
      width: 3,
      color: '#F5A950',
    },
    showSymbol: false,
    symbolSize: 10,
    itemStyle: {
      color: '#F5A950',
    },
    emphasis: {
      focus: 'series',
    },
    data: [] as number[],
    tooltip: {
      valueFormatter: (value: number) => `${yunFormat(value, {
        unit: 'yun',
      })}`,
    },
  },
  ],
})

const { isFetching } = useQuery({
  enabled: computed(() => !!officeCode?.value),
  queryKey: ['company/index/dashboard/statement/trade/today/nearest', officeCode],
  queryFn: () => todayNearest({
    officeCode: officeCode?.value,
  }),
  select(data) {
    const rows = data?.rows || []
    option.value.xAxis[0].data = rows?.map(item => item.dateTime || '')
    option.value.series[0].data = rows?.map(item => item.tradeCount || 0)
    option.value.series[1].data = rows?.map(item => item.turnover ? new Big(item.turnover).div(100).toNumber() : 0)
    return rows
  },
})
</script>

<template>
  <VChart ref="chartRef" autoresize :loading="isFetching" class="h-[300px] mt-6" :option="option" />
</template>
